// Vendor record foundations — cross-loan vendor assignments. The reverse
// LoanPartyAssignment lookup (counterpartyType=Vendor) joined to each loan;
// inactive rows render as Former so swap history stays visible. Per-loan
// management stays on each Loan detail page's Vendors tab.

import { Link } from "react-router-dom";
import {
  Badge,
  Card,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useVendorAssignments } from "@/hooks/portfolioAdmin/useRegistries";
import { useDateFormatter } from "@/utils/datetime";

export function VendorAssignmentsTab({ vendorId }: { vendorId: string }) {
  const { date } = useDateFormatter();
  const query = useVendorAssignments(vendorId);

  if (query.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          Loading assignments…
        </p>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load this vendor&apos;s loan assignments. Refresh to retry.
        </p>
      </Card>
    );
  }

  const rows = query.data ?? [];
  if (rows.length === 0) {
    return (
      <Card data-testid="vendor-assignments-empty">
        <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
          Cross-loan vendor assignments
        </h3>
        <p className="text-small text-gray-mid m-0">
          This vendor isn&apos;t assigned to any loans yet. Assign it from a
          Loan detail page&apos;s <strong>Vendors</strong> tab.
        </p>
      </Card>
    );
  }

  return (
    <Card data-testid="vendor-assignments-tab">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
        Cross-loan vendor assignments
      </h3>
      <Table data-testid="vendor-assignments-table">
        <THead>
          <TR>
            <TH className="text-white-pure">Loan</TH>
            <TH className="text-white-pure">Account state</TH>
            <TH className="text-white-pure">Role on loan</TH>
            <TH className="text-white-pure">Assigned</TH>
            <TH className="text-white-pure">Effective</TH>
            <TH className="text-white-pure">Status</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((r) => (
            <TR key={r.assignmentId} data-testid={`vendor-assignment-${r.assignmentId}`}>
              <TD>
                <Link
                  to={`/portfolio-admin/loans/${r.loanId}`}
                  className="text-gold-shadow hover:underline"
                >
                  {r.loanNumber}
                </Link>
              </TD>
              <TD>{r.accountState ?? "—"}</TD>
              <TD>{r.roleOnLoan}</TD>
              <TD>{date(r.assignedAt)}</TD>
              <TD>
                {r.effectiveFrom || r.effectiveTo
                  ? `${r.effectiveFrom ? date(r.effectiveFrom) : "…"} → ${
                      r.effectiveTo ? date(r.effectiveTo) : "present"
                    }`
                  : "—"}
              </TD>
              <TD>
                {r.isActive ? (
                  <Badge status="finalized">Active</Badge>
                ) : (
                  <Badge status="archived">Former</Badge>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  );
}
